import React, {useEffect, useState} from 'react'
import {useFormik} from 'formik'
import {TextInput} from '../../../../components/inputs/TextInput'
import {SelectInput} from '../../../../components/inputs/SelectInput'
import {
  createDelaisTraitement,
  editDelaisTraitement,
  getAllTypeDelaisTraitement,
  verifyActifDelaisTraitement,
} from '../../../../components/requests'
import {ModeleTypeDelaisTraitement} from '../../../../components/Models'
import {useNavigate} from 'react-router-dom'
import Swal from 'sweetalert2'
import {colors} from '../../../../components/Data'

// Validateur du formulaire durant l'ajout / modification
const validate = (values) => {
  const errors: { libelle?: string; type_delais?: string; duree?: string; unite?: string, couleur?: string } = {}

  // Si il n'y a pas de libellé saisi
  if (!values.libelle) {
    errors.libelle = 'Le libelle est requis'
  }
  // Si il n'y a pas de type de délais séléctionné
  if (!values.type_delais) {
    errors.type_delais = 'Le type de délai est requis'
  }
  // Si il n'y a pas de durée séléctionné
  if (!values.duree) {
    errors.duree = 'La durée est requis'
  } else if (!/^\d+$/.test(values.duree)) {
    // Si la durée un bumbre flottant
    errors.duree = 'La durée est invalide'
  } else if (values.duree <= 0) {
    // Si la durée est inférieur ou égal à 0
    errors.duree = 'La durée doit être supérieur à 0'
  }
  // Si il n'y a pas de type de délais séléctionné
  if (!values.couleur) {
    errors.couleur = 'La couleur est requis'
  }

  return errors
}

/**
 * Formulaire d'ajout / modification d'un délais de traitement
 *
 * id : identifiant du délais de traitement,
 * si id n'est pas null, l'action sera la modification
 * si id est null, l'action sera la création
 *
 * data : données à placer sur les formulaires pour la modification
 * */
const DelaisTraitementForm = ({id, data}) => {
  const [allTypeDelais, setAllTypeDelais] = useState<Array<ModeleTypeDelaisTraitement>>([])
  const [allTypeDelaisFormatted, setAllTypeDelaisFormatted] = useState<Array<any>>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [colorPreview, setColorPreview] = useState<string>('')
  const navigate = useNavigate()

  // Méthode qui prends tous les type de délais
  // Avant Traitement et Traitement
  const getAllTypeDelais = () => {
    getAllTypeDelaisTraitement()
      .then((response) => {
        if (response?.data?.length > 0 && response?.status === 200) {
          // Enregistrement dans un useState de tous les type de délais
          setAllTypeDelais(response?.data)
          // Enregistrement dans un useState de tous les type de délais formatté pour le select
          setAllTypeDelaisFormatted(
            response?.data?.map((typeDelais) => ({
              label: typeDelais.libelle,
              value: typeDelais.code,
            }))
          )
        }
      })
      .catch(() => {
        console.error('erreur')
      })
  }

  // Méthode qui vérifie quel action faire par rapport aux données en props
  const insertOrUpdateDelaisTraitement = (values) => {
    let action
    // Si il n'y a pas de data
    if (data === null) {
      action = createDelaisTraitement(values)
    } else {
      // Si il y a des données dans data
      action = editDelaisTraitement(id, values)
    }
    action
      .then((response) => {
        if (response?.data !== undefined && response?.status === 200) {
          setIsLoading(false)
          // Navigation vers la liste apres soumission avec succès du formulaire
          navigate('/delais-traitement/liste')
        }
      })
      .catch(() => {
        console.error('erreur')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  // Méthode qui effectue l'action à faire apres soumission
  // Verifie aussi si il y a déjà un actif en cours
  const submitDelaisTraitement = (values) => {
    setIsLoading(true)
    // Si la valeur soumis est actif
    if (values.actif === true) {
      //si actif est true et qu'il n'est pas modifié , on affiche pas le swal ( lors de la modification )
      if (data !== null && data.actif === true) {
        if (data.actif === values.actif) {
          if (values.type_delais === data.typeDelaiTraitementDemande.code) {
            const updatedValue = {...values, isConfirmed: false}
            insertOrUpdateDelaisTraitement(updatedValue)
            return
          }
        }
      }
      // Verifie si il y a des autres délais de traitement actif
      verifyActifDelaisTraitement(values.type_delais)
        .then((response) => {
          // Si il y a d'autre délais de traitement actif
          if (
            response?.data !== undefined &&
            response?.status === 200 &&
            response?.data.length > 0
          ) {
            // Afficher un popup de confirmation pour la création du nouveau délais sur le type de délais actif
            Swal.fire({
              title: 'Attention',
              text: 'Vous avez déjà un délai de traitement actif de ce type. Voulez-vous le confirmer comme étant actif?',
              icon: 'warning',
              confirmButtonText: 'Confirmer',
              cancelButtonText: 'Annuler',
              showCancelButton: true,
              confirmButtonColor: '#1BC5BD',
              cancelButtonColor: '#F64E60',
            })
              .then((result) => {
                if (result.isConfirmed) {
                  setIsLoading(true)
                  const updatedValue = {...values, isConfirmed: true}
                  insertOrUpdateDelaisTraitement(updatedValue)
                }
              })
              .catch(() => {
                console.error('erreur')
              })
            setIsLoading(false)
          } else {
            // Si il n'y a pas de délais de traitement actif
            const updatedValue = {...values, isConfirmed: false}
            insertOrUpdateDelaisTraitement(updatedValue)
          }
        })
        .catch(() => {
          console.error('erreur')
        })
    } else {
      // Si la valeur soumis est n'est pas actif
      const updatedValue = {...values, isConfirmed: false}
      insertOrUpdateDelaisTraitement(updatedValue)
    }
  }

  // useEffect qui prend tous les donénes dans type détails de traitement
  useEffect(() => {
    getAllTypeDelais()

    // Définition de l'unité de la durée sur heure par défaut
    formik.setFieldValue('unite', 'h')
  }, [])

  // useEffect qui met les données dans data sur le formulaire
  useEffect(() => {
    if (data !== null) {
      formik.setFieldValue('libelle', data.libelle)
      formik.setFieldValue('type_delais', data.typeDelaiTraitementDemande.code)
      formik.setFieldValue('duree', data.duree)
      formik.setFieldValue('unite', data.unite)
      formik.setFieldValue('couleur', data.couleur)
      formik.setFieldValue('actif', data.actif)

      if (data?.couleur) getPreviewColor(data?.couleur)
    }
  }, [data])

  const getPreviewColor = (e) => {
    const selectedColor = colors
      .find((color) => color.value === (e?.target?.value ?? e))
    if (selectedColor) setColorPreview(selectedColor?.preview)
  }

  // Formik pour valider et soumettre le formulaire
  const formik = useFormik({
    initialValues: {
      libelle: '',
      type_delais: '',
      duree: '',
      unite: '',
      actif: false,
      couleur: '',
    },
    validate,
    onSubmit: (values) => {
      submitDelaisTraitement(values)
    },
  })

  return (
    <div>
      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        <div className='card-body border-top p-9'>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label required fw-bold fs-6'>Libellé</label>
            <div className='col-lg-8'>
              <div className='row'>
                <div className='col-lg-6 fv-row'>
                  <TextInput
                    fieldName={'libelle'}
                    inputPlaceholder={'Libellé'}
                    inputType={'text'}
                    inputClassName={'form-control form-control-lg form-control-solid mb-3 mb-lg-0'}
                    formik={formik}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label required fw-bold fs-6'>Type de délai</label>
            <div className='col-lg-8'>
              <div className='row'>
                <div className='col-lg-6 fv-row'>
                  <SelectInput
                    fieldName={'type_delais'}
                    inputClassName={'form-select form-select-solid fw-bold'}
                    options={allTypeDelaisFormatted}
                    disabled={allTypeDelais?.length === 0}
                    defaultValue={'Selectionner un type de délai ...'}
                    formik={formik}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label required fw-bold fs-6'>Durée</label>
            <div className='col-lg-8'>
              <div className='row'>
                <div className='col-lg-3 fv-row'>
                  <TextInput
                    fieldName={'duree'}
                    inputPlaceholder={'Durée'}
                    inputType={'number'}
                    min={1}
                    inputClassName={'form-control form-control-lg form-control-solid mb-3 mb-lg-0'}
                    formik={formik}
                  />
                </div>
                <div className='col-lg-3 fv-row'>
                  <SelectInput
                    fieldName={'unite'}
                    inputClassName={'form-select form-select-solid fw-bold'}
                    options={[
                      {value: 'j', label: 'Jour'},
                      {value: 'h', label: 'Heure'},
                      {value: 'mn', label: 'Minute'},
                    ]}
                    formik={formik}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label required fw-bold fs-6'>Couleur</label>
            <div className='col-lg-8'>
              <div className='row'>
                <div className='col-lg-6 fv-row position-relative'>
                  <SelectInput
                    fieldName={'couleur'}
                    inputClassName={'form-select form-select-solid fw-bold'}
                    options={colors}
                    defaultValue={'Selectionner une couleur ...'}
                    formik={formik}
                    onChange={(e) => getPreviewColor(e)}
                  />
                  {(colorPreview) && (
                    <div
                      style={{
                        position: 'absolute',
                        right: '50px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '75px',
                        height: '30px',
                        backgroundColor: colorPreview || '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                        zIndex: 2,
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>Actif</label>
            <div className='col-lg-8'>
              <div className='row'>
                <div className='col-lg-6 fv-row mt-1'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    name={'actif'}
                    checked={formik.values?.actif}
                    defaultChecked={formik.values?.actif}
                    onChange={(e) => {
                      formik.setFieldValue('actif', e.target.checked)
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button
            type='button'
            className='btn btn-light me-3'
            onClick={() => {
              formik.resetForm()
              navigate(`/delais-traitement/liste`)
            }}
          >
            Annuler
          </button>
          <button type='submit' className='btn btn-primary'>
            {isLoading ? (
              <span className='indicator-progress' style={{display: 'block'}}>
                Enregistrement...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            ) : (
              'Enregistrer'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export {DelaisTraitementForm}
